import React from "react";
import {autobind} from "core-decorators";
import {Table, Typography} from "antd";
import {AudioOutlined} from "@ant-design/icons";
import {action, observable} from "mobx";
import {inject, observer} from "mobx-react";
import {gql} from "@apollo/client";
import {RouteComponentProps} from "react-router-dom";
import {DateTime} from "luxon";

import "./Rehearsals.scss";
import {RehearsalsQuery, RehearsalsQueryVariables} from "@/graphql/types";
import AudioButton from "@/components/AudioButton/AudioButton";
import PageLayout from "@/components/PageLayout/PageLayout";
import RehearsalsActions from "./RehearsalsActions";
import RehearsalsConductorsNotes from "./RehearsalsConductorsNotes";
import RehearsalsNotes from "./RehearsalsNotes";
import RehearsalsVisibility from "./RehearsalsVisibility";
import Store from "@/stores";

export interface IRehearsalsProps extends RouteComponentProps {
  store?: Store;
}

@inject("store")
@autobind
@observer
export default class Rehearsals extends React.Component<IRehearsalsProps> {
  @observable rehearsals?: RehearsalsQuery["rehearsals"];

  async componentDidMount() {
    const {apollo, session} = this.props.store!;
    const result = await apollo.client.query<RehearsalsQuery, RehearsalsQueryVariables>({
      query: gql`
        query RehearsalsQuery($userId: Int!, $choirId: Int!) {
          rehearsals(
            where: {
              user_id: {_eq: $userId}
              part: {
                id: {_is_null: false} # Ensure the user has permission to the associated part
                piece: {
                  deleted: {_eq: false} # Do not show rehearsals of deleted pieces
                  choir_id: {_eq: $choirId} # Only show rehersals of the selected ensemble
                }
              }
            }
            order_by: {created_at: desc}
          ) {
            id
            audio
            comment
            conductor_comment
            created_at
            favorite
            start_bar
            end_bar
            visibility
            part {
              piece {
                id
                title
                tempo
                bars
                deleted
              }
              id
              name
            }
          }
        }
      `,
      variables: {userId: session.voicefulUser!.id, choirId: session.selectedEnsemble!.id},
    });
    this.setRehearsals(result.data.rehearsals);
  }

  @action
  setRehearsals(rehearsals?: RehearsalsQuery["rehearsals"]) {
    this.rehearsals = rehearsals;
  }

  public render() {
    return (
      <PageLayout>
        <div className="Rehearsals">
          <Typography.Title level={3}>
            <AudioOutlined className="title-icon" />
            Rehearsals
          </Typography.Title>
          <Table<RehearsalsQuery["rehearsals"][0]>
            // we need to create a new array to trigger a re-render of the table component
            dataSource={this.rehearsals?.slice()}
            rowKey="id"
            loading={!this.rehearsals}
            pagination={false}
            scroll={{x: true}}
          >
            <Table.Column<RehearsalsQuery["rehearsals"][0]>
              title="Actions"
              key="actions"
              render={(_, rehearsal) => <RehearsalsActions rehearsal={rehearsal} rehearsals={this.rehearsals!} />}
            />
            <Table.Column<RehearsalsQuery["rehearsals"][0]> title="Piece" dataIndex={["part", "piece", "title"]} />
            <Table.Column<RehearsalsQuery["rehearsals"][0]> title="Part" dataIndex={["part", "name"]} />
            <Table.Column<RehearsalsQuery["rehearsals"][0]>
              title="Bars"
              key="bars"
              render={(_, rehearsal) => {
                const rehearsalBars = rehearsal.end_bar - rehearsal.start_bar;
                const complete = rehearsal.part.piece.bars === rehearsalBars ? " [complete]" : "";
                return `${rehearsal.start_bar}-${rehearsal.end_bar}${complete}`;
              }}
            />
            <Table.Column<RehearsalsQuery["rehearsals"][0]>
              title="Tempo"
              key="tempo"
              render={(_, rehearsal) => rehearsal.part.piece.tempo && `${rehearsal.part.piece.tempo} bpm`}
            />
            <Table.Column<RehearsalsQuery["rehearsals"][0]>
              title="Date"
              key="created_at"
              render={(_, rehearsal) => DateTime.fromISO(rehearsal.created_at).toFormat("yyyy-LL-dd HH:mm:ss")}
            />
            <Table.Column<RehearsalsQuery["rehearsals"][0]>
              title="Visibility"
              key="visibility"
              render={(_, rehearsal) => <RehearsalsVisibility rehearsal={rehearsal} />}
            />
            <Table.Column<RehearsalsQuery["rehearsals"][0]>
              title="Audio"
              key="audio"
              render={(_, rehearsal) => (
                <div className="center">
                  <AudioButton url={rehearsal.audio} />
                </div>
              )}
            />
            <Table.Column<RehearsalsQuery["rehearsals"][0]>
              title="My notes"
              key="notes"
              render={(_, rehearsal) => <RehearsalsNotes rehearsal={rehearsal} />}
            />
            <Table.Column<RehearsalsQuery["rehearsals"][0]>
              title="Conductor's notes"
              key="conductor_comment"
              render={(_, rehearsal) => <RehearsalsConductorsNotes rehearsal={rehearsal} />}
            />
          </Table>
        </div>
      </PageLayout>
    );
  }
}
